import React from 'react';
import Icon from '../../../components/AppIcon';

const GoogleReviews = ({ reviews, businessName, averageRating, totalReviews }) => {
  if (!reviews || reviews?.length === 0) {
    return null;
  }

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={16}
        className={index < Math.floor(rating) ? "text-yellow-400 fill-current" : "text-gray-300"}
      />
    ));
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
    return `${Math.floor(diffInDays / 365)} years ago`;
  };

  return (
    <div className="bg-white rounded-xl border border-border shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Icon name="MessageSquare" size={20} className="text-blue-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-foreground">Google Reviews</h3>
              <p className="text-sm text-muted-foreground">
                Customer feedback for {businessName}
              </p>
            </div>
          </div>
          
          {/* Google Badge */}
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-white rounded-lg border border-border flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-5 h-5">
                <path fill="#4285f4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34a853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#fbbc05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#ea4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            </div>
            <span className="text-sm font-medium text-muted-foreground">Verified</span>
          </div>
        </div>

        {/* Overall Rating Summary */}
        <div className="flex items-center space-x-4 mt-4">
          <div className="flex items-center space-x-1">
            {renderStars(averageRating)}
            <span className="ml-2 text-2xl font-bold text-foreground">
              {averageRating?.toFixed(1)}
            </span>
          </div>
          <div className="text-sm text-muted-foreground">
            Based on {totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}
          </div>
        </div>
      </div>

      {/* Reviews List */}
      <div className="p-6 space-y-6">
        {reviews?.map((review, index) => (
          <div key={review?.id || index} className="border-b border-border last:border-b-0 pb-6 last:pb-0">
            {/* Review Header */}
            <div className="flex items-start space-x-4">
              {/* Avatar */}
              <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-accent/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-medium text-foreground">
                  {review?.authorName?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              
              {/* Review Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="font-medium text-foreground">{review?.authorName}</h4>
                    <div className="flex items-center space-x-2 mt-1">
                      <div className="flex items-center space-x-1">
                        {renderStars(review?.rating)}
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {getTimeAgo(review?.date)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Review Type Badge */}
                  {review?.verified && (
                    <div className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                      Verified
                    </div>
                  )}
                </div>
                
                {/* Review Text */}
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {review?.text}
                </p>
                
                {/* Helpful Actions */}
                <div className="flex items-center space-x-4 mt-3">
                  <button className="flex items-center space-x-1 text-xs text-muted-foreground hover:text-foreground transition-gentle">
                    <Icon name="ThumbsUp" size={12} />
                    <span>Helpful ({review?.helpfulCount || 0})</span>
                  </button>
                  
                  {review?.ownerResponse && (
                    <button className="flex items-center space-x-1 text-xs text-primary hover:text-primary/80 transition-gentle">
                      <Icon name="MessageCircle" size={12} />
                      <span>Owner response</span>
                    </button>
                  )}
                </div>
                
                {/* Owner Response */}
                {review?.ownerResponse && (
                  <div className="mt-4 p-4 bg-accent/10 rounded-lg border-l-4 border-accent">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="User" size={16} className="text-accent" />
                      <span className="text-sm font-medium text-accent">Business Owner</span>
                      <span className="text-xs text-muted-foreground">
                        {getTimeAgo(review?.ownerResponse?.date)}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {review?.ownerResponse?.text}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* View All Reviews Link */}
      {totalReviews > reviews?.length && (
        <div className="p-6 border-t border-border">
          <button className="w-full flex items-center justify-center space-x-2 py-3 px-4 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted/50 transition-gentle">
            <Icon name="ExternalLink" size={16} />
            <span>View all {totalReviews} reviews on Google</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default GoogleReviews;